import {
  DesignerRequestVerb,
  FileFolderType,
  FileFolderTypeWithContent,
  FolderRootPath,
  FolderRootType,
  IAddFolderRequest,
  IAddFolderResponse,
  IAppSettingsResponse,
  ICheckUpdatesRequest,
  ICheckUpdatesResponse,
  IDropFileRequest,
  IDropFileResponse,
  IDropFolderRequest,
  IDropFolderResponse,
  IFileInfoResponse,
  IGetFileInfo,
  IGetFileRequest,
  IGetFileResponse,
  IGetFolderRequest,
  IGetFolderResponse,
  IInstallUpdatesRequest,
  ILoginResponse,
  IRenameFileRequest,
  IRenameFileResponse,
  IRenameFolderRequest,
  IRenameFolderResponse,
  ISetFileContentRequest,
  ISharedCSSResponse,
  ISavedConditionsResponse,
  ISharedDatasourcesResponse,
  RemoteDebuggerMode,
  IUserPrivilegesResponse,
} from '~shared/types/api';
import { IMonacoThemeResponse } from '~shared/types/code-editor';
import { IComponentTemplate } from '~shared/types/webform-editor';
import { getTypeFromExtension } from '~shared/utils/explorer';
import { queue } from '~shared/utils/waiter';
import { API_PREFIX, EXT_API_PREFIX, http } from './common';
import { sanitizeContent } from '~shared/utils/parser';
import { SerializedNode } from '@ws-ui/craftjs-core';
import { generate } from 'short-uuid';
import { entries } from 'lodash';
import { getSharedResource, IGetSharedResourceRequest } from './shared';

/**
 * Check updates
 * @param request The request
 */
export function checkUpdates(request: ICheckUpdatesRequest) {
  return http<ICheckUpdatesResponse>(EXT_API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });
}

/**
 * Install updates
 * @param request The request
 */
export function installUpdates(request: IInstallUpdatesRequest) {
  return http<IInstallUpdatesRequest>(EXT_API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });
}

export const getDefaultAppSettings = () => {
  const { host, protocol } = window.location;
  const hasSSL = protocol === 'https:';

  return {
    projectOpened: false,
    remoteDebuggerMode: RemoteDebuggerMode.DISABLED,
    baseUrl: '',
    qodly: true,
    urls: {
      renderer: '/$lib/renderer/',
      debugger: `ws${hasSSL ? 's' : ''}://${host}/remoteDebugger`,
      lsp: `ws${hasSSL ? 's' : ''}://${host}/LSP`,
    },
    isRestActive: true,
  };
};

/**
 * Get current application settings
 **/
export async function getAppSettings(): Promise<IAppSettingsResponse> {
  const { hostname, host, protocol } = window.location;
  const hasSSL = protocol === 'https:';

  try {
    const result = await http<{
      rendererPath: string;
      webServerHTTPPort: number;
      webServerHTTPSPort: number;
      remoteDebuggerMode: RemoteDebuggerMode;
      isRESTActive: boolean;
      qodly: boolean;
    }>(API_PREFIX, {
      method: 'POST',
      body: JSON.stringify({
        verb: DesignerRequestVerb.GET_APP_SETTINGS,
      }),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });

    const baseUrl = (() => {
      if (result.rendererPath) {
        return result.rendererPath;
      }

      const httpUrl = `http://${hostname}:${result.webServerHTTPPort}`;
      const httpsUrl = `https://${hostname}:${result.webServerHTTPSPort}`;

      if (result.webServerHTTPPort && result.webServerHTTPSPort) {
        return hasSSL ? httpsUrl : httpUrl;
      }

      return result.webServerHTTPPort ? httpUrl : httpsUrl;
    })();

    return {
      projectOpened:
        result.webServerHTTPSPort > 0 || result.webServerHTTPPort > 0,
      remoteDebuggerMode: result.remoteDebuggerMode,
      baseUrl,
      qodly: typeof result.qodly === 'undefined' ? true : result.qodly,
      urls: {
        renderer: result.rendererPath || `${baseUrl}/$lib/renderer/`,
        debugger: `ws${hasSSL ? 's' : ''}://${host}/remoteDebugger`,
        lsp: `ws${hasSSL ? 's' : ''}://${host}/LSP`,
      },
      isRestActive: result.isRESTActive,
    };
  } catch (err) {
    console.error(err);
    return getDefaultAppSettings();
  }
}

/**
 * Get folder content
 * @param request The request
 */
export async function getFolder(request: IGetFolderRequest) {
  const result = await http<IGetFolderResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });

  // result.children = result.children.map(transformDates(/*'date'*/));
  return result;
}

/**
 * Get folder content
 * @param request The request
 */
export async function getFolders(...roots: FolderRootType[]) {
  const result = await Promise.all(
    roots.map((root) =>
      getFolder({
        root,
        verb: DesignerRequestVerb.GET_FOLDER,
      }),
    ),
  );
  const files = result
    .map(({ children }) =>
      children.map(({ name, date, type }) => ({
        name: name,
        type: type as FileFolderTypeWithContent,
        date: date!,
        path: `${type}/${name}`,
      })),
    )
    .flat();

  return files;
}

/**
 * Get a webform
 * @param request The request
 */
export function getWebForm(
  webform: string,
  options: Partial<{ sanitize: boolean }> = {},
) {
  const { sanitize = true } = options;
  return http<any>(`/rest/$getWebForm/${webform}`, {
    method: 'GET',
  }).then((content) => {
    if (!content?.components || !sanitize) {
      return content;
    }
    const loaderIdsMap = new Map<string, string>(); // old loader id => new loader id
    return {
      ...content,
      // generate a unique id for webform loaders (fixes qs#397)
      // since the same webform loader might be used inside another webform loader
      components: entries<SerializedNode>(content.components)
        .map(([key, comp]) => {
          const originalID = key;
          const { resolvedName } = comp.type as {
            resolvedName: string;
          };
          if (resolvedName === 'WebformLoader') {
            // generate a new id & keep track of the old one
            const newID = generate();
            loaderIdsMap.set(key, newID);
            key = newID;
          }
          return [key, comp, originalID] as [string, SerializedNode, string];
        })
        .reduce((acc, [key, comp, originalID]) => {
          if (comp.nodes) {
            // update old ids in sub nodes
            comp.nodes = comp.nodes.map((id) => loaderIdsMap.get(id) || id);
          }
          if (comp.linkedNodes) {
            // update old ids in linked nodes
            comp.linkedNodes = entries(comp.linkedNodes).reduce(
              (acc, [key, linkedNodeId]) => {
                return {
                  ...acc,
                  [key]: loaderIdsMap.get(linkedNodeId) || linkedNodeId,
                };
              },
              {},
            );
          }
          if (comp.props?.events) {
            comp.props.events = comp.props.events.map(
              (event: webforms.WEvent) => {
                if (event.type === 'navigation' && event.loader) {
                  // update old ids in navigation events
                  event.loader = loaderIdsMap.get(event.loader) || event.loader;
                }
                return event;
              },
            );
          }
          return {
            ...acc,
            [key]: {
              ...comp,
              props: {
                ...comp.props,
                __originalID: originalID,
              },
            },
          };
        }, {}),
    };
  });
}

/**
 * Get file content
 * @param request The request
 */
export function getFile<T = any>(request: IGetFileRequest) {
  return http<IGetFileResponse<T>>(API_PREFIX, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(request),
  }).then((response: IGetFileResponse) => ({
    ...response,
    content: [FileFolderType.METHOD, FileFolderType.CLASS].includes(
      response.type,
    )
      ? sanitizeContent(response.content)
      : response.content,
  }));
}

/**
 * Rename a file
 * @param request The request
 */
export function renameFile(request: IRenameFileRequest) {
  if (request.type === 'folder') {
    return http<IRenameFileResponse>(EXT_API_PREFIX, {
      method: 'POST',
      body: JSON.stringify(request),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });
  }

  return http<IRenameFileResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });
}

/**
 * Drop a file
 * @param request The request
 */
export async function dropFile(request: IDropFileRequest) {
  if ((request.type as string) === 'folder') {
    const result = await http<IDropFileResponse>(EXT_API_PREFIX, {
      method: 'POST',
      body: JSON.stringify(request),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });

    return result.deleted;
  }

  const result = await http<IDropFileResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });

  return result.deleted;
}

/**
 * Add folder
 * @param request The request
 */
export function addFolder(request: IAddFolderRequest) {
  return http<IAddFolderResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });
}

/**
 * Rename a folder
 * @param request The request
 */
export function renameFolder(request: IRenameFolderRequest) {
  return http<IRenameFolderResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });
}

/**
 * Drop Folder
 * @param request The request
 */
export async function dropFolder(request: IDropFolderRequest) {
  const result = await http<IDropFolderResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });

  return result.deleted;
}

export async function getFilesInfo(...files: IGetFileInfo[]) {
  const result = await http<IFileInfoResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify({
      verb: DesignerRequestVerb.GET_FILES_INFO,
      files,
    }),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });

  return result;
}

export async function login(token: string) {
  const result = await http<ILoginResponse>('/api/login', {
    method: 'POST',
    body: JSON.stringify({
      accessKey: token,
    }),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });
  return result.isLogged && result.success;
}

export async function getCatalog() {
  const result = await http<datasources.ICatalog>(
    '/rest/$catalog/$all?$metadata=full',
    {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    },
  );
  return result;
}

interface IGetFileContentInfo {
  name: string;
  path?: string;
  root?: 'root' | 'shared' | 'method';
  asText?: boolean;
}

export interface IFileContentResponse<T = string> {
  name: string;
  date: string;
  content: T;
}

export async function getFileContent<T = string>({
  name,
  path = '',
  root = 'root',
  asText = false,
}: IGetFileContentInfo) {
  return http<IFileContentResponse<T>>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify({
      verb: 'getFileContent',
      root,
      path,
      name,
      asText,
    }),
    headers: new Headers({ 'Content-Type': 'application/json' }),
  });
}

export interface ISetFileContentResponse {
  name: string;
  date: string;
  type: FileFolderTypeWithContent;
  size: number;
}

export async function getSharedDatasources(
  params: Pick<IGetSharedResourceRequest, 'createIfNotExists'> = {},
): Promise<ISharedDatasourcesResponse> {
  try {
    const response = await getSharedResource<ISharedDatasourcesResponse>({
      path: 'shared_datasources.json',
      ...params,
    });
    return response;
  } catch (e) {
    return {};
  }
}

export async function getSharedCSS(
  params: Pick<IGetSharedResourceRequest, 'createIfNotExists'> = {},
): Promise<ISharedCSSResponse> {
  try {
    const response = await getSharedResource<ISharedCSSResponse>({
      path: 'shared_css.json',
      ...params,
    });
    return response;
  } catch (e) {
    return { classes: [] };
  }
}

export async function getSavedConditions(
  params: Pick<IGetSharedResourceRequest, 'createIfNotExists'> = {},
): Promise<ISavedConditionsResponse> {
  try {
    const response = await getSharedResource<ISavedConditionsResponse>({
      path: 'saved_conditions.json',
      ...params,
    });
    return response;
  } catch (e) {
    return { conditions: [] };
  }
}

export async function getUserPrivileges(): Promise<IUserPrivilegesResponse> {
  try {
    const result = await http<IUserPrivilegesResponse>(
      `/rest/$info/privileges`,
      {
        method: 'GET',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
    );
    return result;
  } catch (e) {
    return { privileges: [] };
  }
}

export async function getMonacoTheme(): Promise<IMonacoThemeResponse> {
  try {
    const result = await fetch(`monaco_theme.json?t=${Date.now()}`);
    const theme = (await result.json()) as IMonacoThemeResponse;
    return theme;
  } catch (e) {
    return null;
  }
}

const sharedSourcesQueue = queue();

export const setSharedSources = async (datasources: {
  [key: string]: datasources.ICreateDataSource[];
}): Promise<ISharedDatasourcesResponse> => {
  const payload: ISetFileContentRequest = {
    verb: DesignerRequestVerb.SET_FILE_CONTENT,
    asText: false,
    name: 'shared_datasources.json',
    path: '',
    root: 'shared',
    content: datasources,
  };
  return sharedSourcesQueue(async () => {
    await http<ISetFileContentResponse>(API_PREFIX, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });
    return datasources;
  });
};

export const setFileContent = async (
  payload: Pick<ISetFileContentRequest, 'content' | 'name'> &
    Partial<Pick<ISetFileContentRequest, 'asText' | 'path'>>,
) => {
  const result = await http<ISetFileContentResponse>(API_PREFIX, {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      verb: DesignerRequestVerb.SET_FILE_CONTENT,
      root: FolderRootType.ROOT,
    }),
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  });

  const type = getTypeFromExtension(
    `${payload.path || 'Sources'}/${payload.name}`,
  );

  return {
    ...result,
    type,
    size: 0,
  };
};

export const setCraftedComponents = async (
  crafts: IComponentTemplate[],
): Promise<ISetFileContentResponse> => {
  const apiResponse = await setFileContent({
    name: 'crafted_components.json',
    path: FolderRootPath.WEBFORMS,
    content: crafts,
  });
  return apiResponse;
};
